import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  InputLabel,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ErrorPage from "../Components/ErrorPage";
import PayrollAdd from "./PayrollAdd";
import PayrollEdit from "./PayrollEdit";
import { useTranslation } from "react-i18next";
import useDelete from "../../api/useDelete";
import { printPayroll } from "../utils/printPayroll";

import {
  formatJalaliDateTime,
  formatJalaliMonthName,
  formatJalaliYearFromGregorianYear,
  formatJalaliYearMonth,
} from "../utils/dateFormat";
const PayrollIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, index) => currentYear - index);
  const monthOptions = Array.from({ length: 12 }, (_, index) => {
    const value = index + 1;
    return { value, label: formatJalaliMonthName(value, currentYear) };
  });

  // Pagination and filtering states
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const { handleDelete, ConfirmDialog } = useDelete("shop/payrolls", token);

  // Fetch payroll records with search and pagination.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    [
      "payroll",
      searchTerm,
      selectedEmployee,
      selectedYear,
      selectedMonth,
      page,
      rowsPerPage,
    ],
    `shop/payrolls/?search=${searchTerm}&employee=${selectedEmployee}&year=${selectedYear}&month=${selectedMonth}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Fetch employees for dropdown filter
  const { data: employeesData } = useFetchObjects(
    ["employees"],
    "shop/employees/?page_size=1000",
    token
  );
  const employees = Array.isArray(employeesData)
    ? employeesData
    : employeesData?.results || [];

  useEffect(() => {
    refetch();
  }, [
    searchTerm,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    page,
    rowsPerPage,
    refetch,
  ]);

  // Determine payroll list and total count (using DRF paginated response)
  const payrolls = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : payrolls.length;

  if (isError) {
    return <ErrorPage message={t("payroll.failedToLoadPayrolls")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
    setPage(1);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setPage(1);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setPage(1);
  };

  const formatAmount = (value) => Number(value || 0).toFixed(2);
  const getPayrollView = (payroll, index) => {
    const salary = Number(payroll?.employee_details?.salary || 0);
    const advanceDeduction = Number(payroll?.advance_deduction || 0);
    const netSalary = Number(
      payroll?.net_salary ?? Math.max(salary - advanceDeduction, 0)
    );
    const paymentAmount = Number(payroll?.amount || 0);
    const remainingPayment = Number(
      payroll?.remaining_payment_balance ??
        Math.max(netSalary - paymentAmount, 0)
    );
    const paymentStatus =
      payroll?.payment_status ||
      (remainingPayment > 0 ? "Pending" : "Completed");
    const currency = payroll?.currency_details?.title || "";

    return {
      displayId: (page - 1) * rowsPerPage + index + 1,
      branch: payroll?.shop_details?.name || t("payroll.unknownShop"),
      employee: payroll?.employee_details?.name || t("payroll.unknownEmployee"),
      salary,
      advanceDeduction,
      netSalary,
      paymentAmount,
      remainingPayment,
      paymentStatus,
      currency,
      period: formatJalaliYearMonth(payroll?.year, payroll?.month),
      paymentDate: payroll?.payment_date
        ? formatJalaliDateTime(payroll.payment_date)
        : "--",
      account: payroll?.account_details?.name || "--",
      notes: payroll?.notes || "--",
    };
  };

  const statusColor = (status) =>
    String(status).toLowerCase() === "completed" ? "success" : "warning";

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f5f7fb",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 1.5,
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            color: "#132238",
            letterSpacing: "-0.02em",
          }}
        >
          {t("payroll.payrollList")}
        </Typography>
        <Button
          variant="contained"
          onClick={() => setOpenAdd(true)}
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            minWidth: 142,
            height: 42,
            px: 2.5,
            borderRadius: 1.5,
            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.22)",
            fontWeight: 700,
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          {t("payroll.addPayroll")}
        </Button>
      </Box>
      <Typography
        variant="subtitle1"
        sx={{ color: "#5f6f86", mb: 2.5, fontWeight: 400 }}
      >
        {t("payroll.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>
      {/* Search and Filter Fields */}
      <Box
        sx={{
          mb: 2.5,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr 1fr" },
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("payroll.searchPayrolls")}
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#6f7d90", fontSize: 28 }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
            },
          }}
        >
          <Select
            value={selectedEmployee}
            onChange={handleEmployeeChange}
            displayEmpty
            placeholder={t("payroll.selectEmployee")}
          >
            <MenuItem value="">{t("payroll.selectEmployee")}</MenuItem>
            {employees.map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
            },
          }}
        >
          <InputLabel id="payroll-year-label">
            {t("payroll.year", "Year")} *
          </InputLabel>
          <Select
            labelId="payroll-year-label"
            value={selectedYear}
            onChange={handleYearChange}
            label={`${t("payroll.year", "Year")} *`}
          >
            <MenuItem value="">
              <em>{t("payroll.allYears", "All Years")}</em>
            </MenuItem>
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {formatJalaliYearFromGregorianYear(year)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          variant="outlined"
          sx={{
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-root": {
              height: 64,
              borderRadius: 1,
            },
          }}
        >
          <InputLabel id="payroll-month-label">
            {t("payroll.month", "Month")} *
          </InputLabel>
          <Select
            labelId="payroll-month-label"
            value={selectedMonth}
            onChange={handleMonthChange}
            label={`${t("payroll.month", "Month")} *`}
          >
            <MenuItem value="">
              <em>{t("payroll.allMonths", "All Months")}</em>
            </MenuItem>
            {monthOptions.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Desktop Payroll Table */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 2,
          display: { xs: "none", md: "block" },
          borderRadius: 1.5,
          overflowX: "auto",
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
          border: "1px solid #dbe3ef",
          backgroundColor: "#fff",
        }}
      >
        <Table sx={{ minWidth: 1120 }} aria-label="payroll table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              {[
                t("payroll.payrollId", "ID"),
                t("payroll.shop", "Branch"),
                t("payroll.employee", "Employee"),
                t("payroll.employeeSalary", "Salary"),
                t("payroll.advanceAmount", "Advance"),
                t("payroll.netSalary", "Net Salary"),
                t("payroll.paymentAmount", "Payment"),
                t("payroll.remainingPayment", "Remaining"),
                t("payroll.paymentStatus", "Status"),
                t("payroll.monthYear", "Month/Year"),
                t("payroll.paymentDate", "Payment Date"),
                t("payroll.action", "Action"),
              ].map((heading) => (
                <TableCell
                  key={heading}
                  sx={{ color: "white", fontWeight: 700, borderBottom: "none" }}
                >
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {Array.from(new Array(12)).map((__, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payrolls.map((payroll, index) => {
                  const row = getPayrollView(payroll, index);
                  const currency = row.currency ? ` ${row.currency}` : "";
                  return (
                    <TableRow
                      key={payroll.id}
                      hover
                      sx={{
                        "& td": {
                          py: 2,
                          borderBottom: "1px solid #e7edf5",
                          whiteSpace: "nowrap",
                        },
                        "&:hover": { backgroundColor: "#f8fbff" },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.displayId}</TableCell>
                      <TableCell>{row.branch}</TableCell>
                      <TableCell>{row.employee}</TableCell>
                      <TableCell>{formatAmount(row.salary)}{currency}</TableCell>
                      <TableCell>{formatAmount(row.advanceDeduction)}{currency}</TableCell>
                      <TableCell>{formatAmount(row.netSalary)}{currency}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {formatAmount(row.paymentAmount)}{currency}
                      </TableCell>
                      <TableCell>{formatAmount(row.remainingPayment)}{currency}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.paymentStatus}
                          color={statusColor(row.paymentStatus)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.period}</TableCell>
                      <TableCell>{row.paymentDate}</TableCell>
                      <TableCell sx={{ minWidth: 230 }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PrintIcon fontSize="small" />}
                            onClick={() => printPayroll(payroll)}
                          >
                            {t("payroll.print", "Print")}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditData(payroll);
                              setOpenEdit(true);
                            }}
                          >
                            {t("payroll.edit", "Edit")}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDelete(payroll.id)}
                          >
                            {t("payroll.delete", "Delete")}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile Payroll Cards */}
      <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1.5, mt: 2 }}>
        {isLoading
          ? Array.from(new Array(3)).map((_, index) => (
              <Paper
                key={`mobile-skeleton-${index}`}
                sx={{ p: 2, borderRadius: 1.5, border: "1px solid #dbe3ef" }}
              >
                <Skeleton width="45%" />
                <Skeleton width="70%" />
                <Skeleton width="100%" />
                <Skeleton width="80%" />
              </Paper>
            ))
          : payrolls.map((payroll, index) => {
              const row = getPayrollView(payroll, index);
              const currency = row.currency ? ` ${row.currency}` : "";
              return (
                <Paper
                  key={payroll.id}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: "1px solid #dbe3ef",
                    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: "#132238" }}>
                        {row.employee}
                      </Typography>
                      <Typography sx={{ color: "#5f6f86", fontSize: 13 }}>
                        {row.branch} | {row.period}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={row.paymentStatus}
                      color={statusColor(row.paymentStatus)}
                      variant="outlined"
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    {[
                      [t("payroll.employeeSalary", "Salary"), row.salary],
                      [t("payroll.advanceAmount", "Advance"), row.advanceDeduction],
                      [t("payroll.netSalary", "Net Salary"), row.netSalary],
                      [t("payroll.paymentAmount", "Payment"), row.paymentAmount],
                      [t("payroll.remainingPayment", "Remaining"), row.remainingPayment],
                    ].map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: "#f5f7fb",
                        }}
                      >
                        <Typography sx={{ color: "#5f6f86", fontSize: 12 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ color: "#132238", fontWeight: 700 }}>
                          {formatAmount(value)}{currency}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Typography sx={{ color: "#5f6f86", fontSize: 13, mb: 1.5 }}>
                    {t("payroll.paymentDate", "Payment Date")}: {row.paymentDate}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PrintIcon fontSize="small" />}
                      onClick={() => printPayroll(payroll)}
                    >
                      {t("payroll.print", "Print")}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setEditData(payroll);
                        setOpenEdit(true);
                      }}
                    >
                      {t("payroll.edit", "Edit")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDelete(payroll.id)}
                    >
                      {t("payroll.delete", "Delete")}
                    </Button>
                  </Box>
                </Paper>
              );
            })}
      </Box>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="18px 10px 6px"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ color: "#132238", fontSize: 16 }}>
            {t("payroll.rowsPerPage", "Rows Per Page")}
          </Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 8,
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          shape="rounded"
          sx={{
            "& .MuiPaginationItem-root": {
              borderRadius: "50%",
            },
          }}
        />
      </Grid>

      {openAdd && (
        <PayrollAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onSaved={() => {
            setOpenAdd(false);
            refetch();
          }}
          token={token}
        />
      )}
      {openEdit && (
        <PayrollEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
          onSaved={() => {
            setOpenEdit(false);
            refetch();
          }}
        />
      )}
      <ConfirmDialog />

    </Box>
  );
};

export default PayrollIndex;
